import { useAuth0 } from '@auth0/auth0-react';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import { Box } from '@material-ui/core';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import Router from 'next/router';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';

import { Flex } from '@/components/Flex';
import EmptyProducts from '@/components/ProductList/EmptyProducts';
import Header from '@/components/Header';
import Table from '@/components/Table';
import Loading from '@/components/Loading';
import ProductTableHeader from '@/components/ProductList/ProductTableHeader';
import ProductImportModal from '@/components/ProductList/ProductImportModal';
import ProductImportProgressModal from '@/components/ProductList/ProductImportProgressModal';
import ProductExportModal from '@/components/ProductList/ProductExportModal';
import ProductDeleteModal from '@/components/ProductList/ProductDeleteModal';
import {
  NEW_FEATURE_FLAG_PRODUCTS_CREATE,
  NEW_FEATURE_FLAG_PRODUCTS_EDIT,
} from '@/constants/newFeatureFlags';
import { useStores } from '@/hooks/useStores';

import {
  fetchFlags,
  LD_FLAG_NEW_FEATURE,
} from '@/utils/fetchLaunchDarklyFlags';
import { buildGridOptions } from '@/utils/masterDetail';
import withTokenRequired from '@/utils/withTokenRequired';

const SNACKBAR_DELETE_DISMISS_MS = 3000;
const GRID_ROW_HEIGHT = 40;

const ProductImportToast = ({ toastText, onExited }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { allProductsPage } = useStores();

  if (!allProductsPage.productImportToastId) {
    const toastId = enqueueSnackbar(toastText, {
      autoHideDuration: SNACKBAR_DELETE_DISMISS_MS,
      onExited,
    });

    allProductsPage.setProductImportToastId(toastId);
  }

  return null;
};

const Products = () => {
  const [fetchedProductCount, setFetchedProductCount] = useState(0);
  const [newFeaturesEnabled, setNewFeaturesEnabled] = useState({});
  const [productCountFetched, setProductCountFetched] = useState(false);
  const { allProductsPage, productStore } = useStores();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useAuth0();
  const masterGridApi = useRef(null);
  const rowClickHandler = useRef(null);
  const ldClient = useLDClient();

  useEffect(() => {
    productStore
      .setTotalProductCount()
      .then(() => {
        setFetchedProductCount(productStore.totalProductCount);
        setProductCountFetched(true);
      })
      .catch(console.error); // TODO: Throw something better
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // resume import job after page refresh
    if (allProductsPage.isImporterJobInProgress) {
      allProductsPage.startPollingImportJobStatus();
    }
  }, [allProductsPage]);

  useEffect(() => {
    const fetchFlagsAsync = async () => {
      const result = await fetchFlags(ldClient, user);

      if (result !== undefined && result[LD_FLAG_NEW_FEATURE] !== undefined) {
        const newFeatures = {};
        result[LD_FLAG_NEW_FEATURE].forEach((f) => {
          newFeatures[f] = true;
        });

        if (!rowClickHandler.current) {
          rowClickHandler.current = (e) =>
            onRowClicked(e, newFeatures[NEW_FEATURE_FLAG_PRODUCTS_EDIT]);
        }

        masterGridApi.current?.addEventListener(
          'rowClicked',
          rowClickHandler.current
        );

        setNewFeaturesEnabled(newFeatures);
      }
    };
    fetchFlagsAsync();

    return () => {
      masterGridApi.current?.removeEventListener(
        'rowClicked',
        rowClickHandler.current
      );
    };
  }, [ldClient, user]);

  if (!productCountFetched) {
    return <Loading />;
  }

  // If they have zero products but have started an import, take them to the empty view as their import occurs
  if (
    productCountFetched &&
    fetchedProductCount < 1 &&
    !allProductsPage.isImporterJobInProgress
  ) {
    return (
      <>
        <EmptyProducts />
        <ProductImportModal />
      </>
    );
  }

  // Table header event handlers
  const handleColumnChange = (column, visibility) => {
    masterGridApi?.current?.columnController.setColumnVisible(
      column,
      visibility
    );
    allProductsPage.toggleColumn(column);
  };

  const handleSearchQuery = (query) => {
    const filterInstance = masterGridApi.current.getFilterInstance('name');
    filterInstance.setModel({
      type: 'contains',
      filter: query,
    });
    masterGridApi.current.onFilterChanged();
  };

  // Master grid event handlers
  const onDeleteClick = async () => {
    const productsToDelete = allProductsPage.selectedProducts;
    const numProductsToDelete = productsToDelete.length;
    const productString = `${numProductsToDelete} product${
      numProductsToDelete > 1 ? 's' : ''
    }`;
    const snackbarKey = enqueueSnackbar(`Deleting ${productString}`, {
      persist: true,
    });

    allProductsPage.closeProductDeleteModal();
    await allProductsPage.deleteProducts(productsToDelete);
    productStore.setTotalProductCount();

    closeSnackbar(snackbarKey);
    enqueueSnackbar(`${productString} deleted`, {
      autoHideDuration: SNACKBAR_DELETE_DISMISS_MS,
    });
    masterGridApi.current.purgeServerSideCache();
  };

  const onFirstDataRendered = (params) => {
    params?.api?.sizeColumnsToFit();
  };

  const onMasterGridReady = ({ api }) => {
    productStore.clearSearchResults();
    api.setServerSideDatasource(productStore);
    allProductsPage.setSelectedProducts([]);
    masterGridApi.current = api;

    // to handle re-rendering and showing the grid after the empty page
    if (rowClickHandler.current && !isEmpty(newFeaturesEnabled)) {
      api.removeEventListener('rowClicked', rowClickHandler.current);

      rowClickHandler.current = (e) =>
        onRowClicked(e, newFeaturesEnabled[NEW_FEATURE_FLAG_PRODUCTS_EDIT]);

      api.addEventListener('rowClicked', rowClickHandler.current);
    }
  };

  const onGridSizeChanged = debounce(
    (event) => event.api?.sizeColumnsToFit(),
    200
  );

  const onImportToastExited = () => {
    allProductsPage.removeProductImportToast();
    masterGridApi.current.purgeServerSideCache();
  };

  const onPaginationChanged = (event) => {
    const totalProducts = event?.api?.paginationGetRowCount();
    const pageSize = event?.api?.paginationGetPageSize();
    if (
      event?.api?.paginationGetCurrentPage() + 1 ===
      event?.api?.paginationGetTotalPages()
    ) {
      const finalPageCount = totalProducts % pageSize;
      allProductsPage.setPageProductCount(finalPageCount);
    } else {
      allProductsPage.setPageProductCount(pageSize);
    }
    event?.api?.deselectAll();
    allProductsPage.setSelectedProducts([]);
  };

  // use a function declaration so that it can be hoisted for use in the useEffect above
  /**
   *
   * @param event
   * @param inNewApp
   */
  function onRowClicked(event, inNewApp) {
    const {
      data: { productId, legacyProductsId },
    } = event;

    if (inNewApp) {
      Router.push(
        `/admin/products/[productId]`,
        `/admin/products/${productId}`
      );
    } else {
      window.location.href = `${process.env.NEXT_PUBLIC_OLD_ADMIN_BASE_URI}/product/${legacyProductsId}/detail`;
    }
  }

  const onSelectionChanged = (selectionEvent) => {
    const selectedNodes = selectionEvent.api.getSelectedNodes();
    const selectedAttrs = selectedNodes.map(
      ({ data: { productId, sku, name } }) => ({
        productId,
        sku,
        name,
      })
    );
    allProductsPage.setSelectedProducts(selectedAttrs);
  };

  const onSortChanged = (event) => {
    event?.api?.deselectAll();
    allProductsPage.setSelectedProducts([]);
    event.api.paginationGoToFirstPage();
  };

  const getRowHeight = ({ node }) => {
    if (!node.detail) return GRID_ROW_HEIGHT;
    const variantsCount = toJS(node.data.variants).length;
    const variantsHeight =
      GRID_ROW_HEIGHT * variantsCount + GRID_ROW_HEIGHT * 2;
    const maxHeight = GRID_ROW_HEIGHT * 12;
    return variantsHeight > maxHeight ? maxHeight : variantsHeight;
  };

  const newProductEdit =
    newFeaturesEnabled[NEW_FEATURE_FLAG_PRODUCTS_EDIT] ?? false;
  const { detailCellRendererParams, masterGridColumnDefs } = buildGridOptions(
    allProductsPage,
    newProductEdit
  );

  const masterGridHeader = (
    <ProductTableHeader
      columnDefs={masterGridColumnDefs}
      handleColumnChange={handleColumnChange}
      handleSearchQuery={handleSearchQuery}
    />
  );

  const masterGridOptions = {
    detailCellRendererParams,
    embedFullWidthRows: true,
    getRowHeight,
    getRowNodeId: (data) => `product-id-${data.productId}`,
    isRowMaster: (dataItem) => dataItem?.variants?.length > 1,
    masterDetail: true,
    onDisplayedColumnsChanged: (event) => {
      event?.api?.sizeColumnsToFit();
    },
    onGridSizeChanged,
    onPaginationChanged,
    onSelectionChanged,
    onSortChanged,
    suppressCellSelection: true,
    suppressRowClickSelection: true,
  };

  const headerActions = [
    {
      alwaysVisible: true,
      dataTestId: 'product-list-page-header-add',
      icon: <AddCircleIcon />,
      label: 'Add',
      linkTo: '/admin/products/create',
      redirect: !newFeaturesEnabled[NEW_FEATURE_FLAG_PRODUCTS_CREATE],
      redirectPath: '/product/add',
      type: 'button',
    },
    {
      dataTestId: 'product-list-page-header-export',
      icon: <PublishIcon />,
      label: 'Export',
      onClick: () => {
        const exportAll = true;
        allProductsPage.openProductExportModal(exportAll);
      },
      type: 'button',
    },
    {
      dataTestId: 'product-list-page-header-import',
      icon: <GetAppIcon />,
      label: 'Import',
      onClick: () => {
        allProductsPage.openProductImportModal();
      },
      type: 'button',
    },
  ];

  return (
    <div>
      <Header title="Products" actions={headerActions} />
      <Flex flexDirection="column">
        <Box mx={4}>
          <Table
            columnDefs={masterGridColumnDefs}
            groupByKey={'productId'}
            onFirstDataRendered={onFirstDataRendered}
            onGridReady={onMasterGridReady}
            options={masterGridOptions}
            tableHeaderComponent={masterGridHeader}
          />
        </Box>
        <ProductExportModal />
        <ProductImportModal />
        <ProductDeleteModal onDeleteClick={onDeleteClick} />
        <ProductImportProgressModal />
        {allProductsPage.showProductImportToast && (
          <ProductImportToast
            onExited={onImportToastExited}
            toastText={allProductsPage.productImportToastText}
          />
        )}
      </Flex>
    </div>
  );
};

export default withLDProvider({
  clientSideID: process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(withTokenRequired(observer(Products)));
